import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Link,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Fade,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  YouTube,
  Refresh,
  PlayArrow,
  Download,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { StyledContainer, StyledPaper, StyledTextField, StyledSubmitButton } from './styles';
import { API_URL } from './config';
import axios from 'axios';
import { formatSubscriberCount } from './youtubeUtils';

const PROGRESS_STEPS = [
  { label: 'Analyzing Input Channels', duration: 10000 },
  { label: 'Searching Channels In Our Database', duration: 20000 },
  { label: 'Searching Channels Across YouTube', duration: 30000 },
  { label: 'Find Similar Channels', duration: 30000 },
  { label: 'Finalizing', duration: 30000 },
];

function SimilarChannels() {
  const [channelInput, setChannelInput] = useState('');
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [polling, setPolling] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const pollingIntervalRef = useRef(null);
  const stepTimeoutRef = useRef(null);
  const [rateLimitStatus, setRateLimitStatus] = useState(null);
  const rateLimitPollingRef = useRef(null);

  // Progress animation effect
  useEffect(() => {
    if (polling && jobStatus?.status === 'processing') {
      setCurrentStep(0);

      let stepIndex = 0;
      setCurrentStep(stepIndex);

      const advanceStep = () => {
        stepIndex++;
        if (stepIndex < PROGRESS_STEPS.length) {
          setCurrentStep(stepIndex);
          // Schedule next step after current step's duration
          stepTimeoutRef.current = setTimeout(advanceStep, PROGRESS_STEPS[stepIndex].duration);
        }
        // Last step will continue until loading finishes
      };

      // Start advancing after first step's duration
      if (PROGRESS_STEPS.length > 0 && stepIndex < PROGRESS_STEPS.length - 1) {
        stepTimeoutRef.current = setTimeout(advanceStep, PROGRESS_STEPS[stepIndex].duration);
      }

      return () => {
        if (stepTimeoutRef.current) {
          clearTimeout(stepTimeoutRef.current);
        }
      };
    } else {
      // Reset when loading stops
      setCurrentStep(0);
      if (stepTimeoutRef.current) {
        clearTimeout(stepTimeoutRef.current);
      }
    }
  }, [polling, jobStatus?.status]);

  // Poll for job status
  useEffect(() => {
    if (!jobId) return;

    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/nexlev/similar-channels/${jobId}`);
        const status = response.data;
        setJobStatus(status);

        // Stop polling if job is completed or failed
        if (status.status === 'completed' || status.status === 'failed') {
          setPolling(false);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          // Reset progress steps
          setCurrentStep(0);
          if (stepTimeoutRef.current) {
            clearTimeout(stepTimeoutRef.current);
          }
        }
      } catch (error) {
        console.error('Error polling job status:', error);
      }
    };

    // Poll immediately
    pollStatus();

    // Then poll every 3 seconds
    if (!pollingIntervalRef.current) {
      setPolling(true);
      pollingIntervalRef.current = setInterval(pollStatus, 3000);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setPolling(false);
    };
  }, [jobId]);

  // Poll for rate limit status
  useEffect(() => {
    const fetchRateLimitStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/nexlev/similar-channels/rate-limit/status`);
        setRateLimitStatus(response.data);
      } catch (error) {
        console.error('Error fetching rate limit status:', error);
      }
    };

    // Fetch immediately
    fetchRateLimitStatus();

    // Poll every 10 seconds
    rateLimitPollingRef.current = setInterval(fetchRateLimitStatus, 10000);

    return () => {
      if (rateLimitPollingRef.current) {
        clearInterval(rateLimitPollingRef.current);
        rateLimitPollingRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async () => {
    if (!channelInput.trim()) {
      setError('Please enter at least one channel URL');
      return;
    }

    setLoading(true);
    setError('');
    setJobStatus(null);
    setJobId(null);

    // Parse the input - split by newlines or commas
    const channelUrls = channelInput
      .split(/[\n,]+/)
      .map((url) => url.trim())
      .filter((url) => url.length > 0);

    if (channelUrls.length === 0) {
      setError('Please enter at least one valid channel URL');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/nexlev/similar-channels`, {
        channelUrls,
      });

      setJobId(response.data.jobId);
    } catch (error) {
      console.error('Error creating job:', error);
      setError(error.message || 'Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setChannelInput('');
    setJobId(null);
    setJobStatus(null);
    setError('');
    setPolling(false);
    setCurrentStep(0);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (stepTimeoutRef.current) {
      clearTimeout(stepTimeoutRef.current);
      stepTimeoutRef.current = null;
    }
  };

  const formatTimeUntilRenew = (renewTime) => {
    if (!renewTime) return 'Available';
    const now = Date.now();
    const msUntilRenew = renewTime - now;
    if (msUntilRenew <= 0) return 'Available';

    const minutes = Math.floor(msUntilRenew / 60000);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${minutes}m`;
  };

  const formatTimeSinceRenew = (renewTime) => {
    if (!renewTime) return null;
    const now = Date.now();
    const msSinceRenew = now - renewTime;
    if (msSinceRenew <= 0) return null;

    const minutes = Math.floor(msSinceRenew / 60000);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m ago`;
    }
    return `${minutes}m ago`;
  };

  const getTotalRemainingRequests = () => {
    if (!rateLimitStatus?.tokens) return 0;
    return rateLimitStatus.tokens.reduce((sum, token) => sum + token.remaining, 0);
  };

  const getNextRenewTime = () => {
    if (!rateLimitStatus?.tokens) return null;
    const renewTimes = rateLimitStatus.tokens
      .map((token) => token.renewTime)
      .filter((time) => time !== null);
    if (renewTimes.length === 0) return null;
    return Math.min(...renewTimes);
  };

  const getRenewalStatus = () => {
    if (!rateLimitStatus?.tokens) return null;
    const now = Date.now();
    const renewTimes = rateLimitStatus.tokens
      .map((token) => token.renewTime)
      .filter((time) => time !== null);

    // Check if all tokens are at full capacity (15 remaining) with no active windows
    const allTokensFull = rateLimitStatus.tokens.every(
      (token) => token.remaining === 15 && !token.renewTime,
    );

    if (renewTimes.length === 0 || allTokensFull) {
      // All tokens are fully available (no active windows or all renewed)
      return {
        type: 'available',
        message: 'Refill every 90 minutes',
      };
    }

    const nextRenewTime = Math.min(...renewTimes);
    const msUntilRenew = nextRenewTime - now;

    if (msUntilRenew <= 0) {
      // Renewal has occurred (shouldn't happen due to backend cleanup, but handle it)
      return {
        type: 'renewed',
        message: 'Renewed',
      };
    }

    return {
      type: 'pending',
      message: `Renews in ${formatTimeUntilRenew(nextRenewTime)}`,
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle />;
      case 'failed':
        return <ErrorIcon />;
      default:
        return <CircularProgress size={16} />;
    }
  };

  const handleDownloadExcel = () => {
    if (!jobStatus?.allChannels || jobStatus.allChannels.length === 0) {
      return;
    }

    // Prepare data for Excel export
    const excelData = jobStatus.allChannels.map((channel) => ({
      'Channel Name': channel.channelName || 'N/A',
      'Channel URL': `https://www.youtube.com/channel/${channel.channelId}`,
      Subscribers: channel.subscriberCount || 0,
      Views: channel.viewCount || 0,
      'In The Niche?': '',
    }));

    // Create a new workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Find the column index for "Channel URL" by checking the header row
    let channelUrlColIndex = -1;
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let col = 0; col <= range.e.c; col++) {
      const headerCell = worksheet[XLSX.utils.encode_cell({ r: 0, c: col })];
      if (headerCell && headerCell.v === 'Channel URL') {
        channelUrlColIndex = col;
        break;
      }
    }

    // Add hyperlinks to Channel URL cells if column found
    if (channelUrlColIndex >= 0) {
      for (let row = 1; row <= range.e.r; row++) {
        // Start from row 1 (skip header row 0)
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: channelUrlColIndex });
        const cell = worksheet[cellAddress];
        if (cell && cell.v) {
          const url = cell.v;
          // Convert cell to hyperlink format
          worksheet[cellAddress] = {
            v: url,
            l: { Target: url },
            t: 's', // string type
          };
        }
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Similar Channels');

    // Generate Excel file and download
    const fileName = `similar_channels_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <Container maxWidth="lg">
      <StyledContainer>
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  mb: 1,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Find Similar Channels
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Paste YouTube channel URLs to find similar channels. This tool works the best for
                <strong> English, Spanish, and Korean channels.</strong>
              </Typography>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        {/* Rate Limit Status */}
        {rateLimitStatus &&
          (() => {
            const renewalStatus = getRenewalStatus();
            return (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body1">
                    <strong>{getTotalRemainingRequests()}</strong> channel searches remaining
                    {renewalStatus && (
                      <span>
                        {' • '}
                        {renewalStatus.message}
                      </span>
                    )}
                  </Typography>
                </Box>
              </Alert>
            );
          })()}

        {!jobId ? (
          <StyledPaper>
            <Box sx={{ mb: 3 }}>
              <StyledTextField
                id="channel-input"
                label="Channel URLs"
                multiline
                rows={8}
                fullWidth
                value={channelInput}
                onChange={(e) => setChannelInput(e.target.value)}
                placeholder="Paste channel URLs here (one per line or comma-separated)
Examples:
https://www.youtube.com/@channelhandle
https://www.youtube.com/channel/UC...
https://www.youtube.com/c/channelname"
                disabled={loading}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={handleReset} disabled={loading}>
                Clear
              </Button>
              <StyledSubmitButton
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !channelInput.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
              >
                {loading ? 'Creating Job...' : 'Find Similar Channels'}
              </StyledSubmitButton>
            </Box>
          </StyledPaper>
        ) : (
          <Box>
            {/* Job Status Card */}
            <StyledPaper sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6">Job Status</Typography>
                  {jobStatus && (
                    <Chip
                      icon={getStatusIcon(jobStatus.status)}
                      label={jobStatus.status}
                      color={getStatusColor(jobStatus.status)}
                      size="small"
                    />
                  )}
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleReset}
                  disabled={polling && jobStatus?.status === 'processing'}
                >
                  Start New Search
                </Button>
              </Box>

              {jobStatus && (
                <Box>
                  {/* Progress Steps - Show during processing */}
                  {polling && jobStatus.status === 'processing' && (
                    <Box sx={{ mb: 3, py: 2 }}>
                      {PROGRESS_STEPS.map((step, index) => {
                        const isCompleted = index < currentStep;
                        const isCurrent = index === currentStep;
                        const isUpcoming = index > currentStep;

                        return (
                          <Fade
                            key={index}
                            in={isCompleted || isCurrent}
                            timeout={500}
                            style={{ transitionDelay: isCompleted ? '0ms' : '200ms' }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                mb: 2,
                                opacity: isUpcoming ? 0.3 : 1,
                                transition: 'opacity 0.3s ease-in-out',
                              }}
                            >
                              <Box sx={{ minWidth: 40, display: 'flex', justifyContent: 'center' }}>
                                {isCompleted ? (
                                  <CheckCircle
                                    sx={{
                                      color: 'success.main',
                                      fontSize: 28,
                                      animation: 'scaleIn 0.3s ease-out',
                                      '@keyframes scaleIn': {
                                        '0%': { transform: 'scale(0)', opacity: 0 },
                                        '100%': { transform: 'scale(1)', opacity: 1 },
                                      },
                                    }}
                                  />
                                ) : isCurrent ? (
                                  <CircularProgress
                                    size={28}
                                    thickness={4}
                                    sx={{
                                      color: 'primary.main',
                                    }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: '50%',
                                      border: '2px solid',
                                      borderColor: 'text.disabled',
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: isCompleted
                                    ? 'success.main'
                                    : isCurrent
                                      ? 'primary.main'
                                      : 'text.disabled',
                                  fontWeight: isCurrent ? 600 : 400,
                                  transition: 'all 0.3s ease-in-out',
                                }}
                              >
                                {step.label}
                              </Typography>
                            </Box>
                          </Fade>
                        );
                      })}
                    </Box>
                  )}

                  {/* Status Cards - Show only when completed or failed */}
                  {(jobStatus.status === 'completed' || jobStatus.status === 'failed') && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={4}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" color="primary">
                              {jobStatus.total}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Channels
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" color="success.main">
                              {jobStatus.completed}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Completed
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" color="error.main">
                              {jobStatus.failed}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Failed
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  )}

                  {jobStatus.errors && jobStatus.errors.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="warning">
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          Errors ({jobStatus.errors.length}):
                        </Typography>
                        {jobStatus.errors.slice(0, 5).map((err, idx) => (
                          <Typography key={idx} variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {err.url || err.channelId}: {err.error}
                          </Typography>
                        ))}
                        {jobStatus.errors.length > 5 && (
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', mt: 1 }}>
                            ... and {jobStatus.errors.length - 5} more errors
                          </Typography>
                        )}
                      </Alert>
                    </Box>
                  )}
                </Box>
              )}
            </StyledPaper>

            {/* Results */}
            {jobStatus?.status === 'completed' && jobStatus.allChannels && (
              <StyledPaper>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">
                    Similar Channels Found ({jobStatus.allChannels.length})
                  </Typography>
                  {jobStatus.allChannels.length > 0 && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Download />}
                      onClick={handleDownloadExcel}
                    >
                      Download as Excel Sheet
                    </Button>
                  )}
                </Box>

                {jobStatus.allChannels.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No similar channels found that meet the minimum criteria (500+ subscribers, 50k+
                    views).
                  </Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Channel Name</TableCell>
                          <TableCell>Subscribers</TableCell>
                          <TableCell>Views</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jobStatus.allChannels.map((channel, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Link
                                href={`https://www.youtube.com/channel/${channel.channelId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                              >
                                <YouTube sx={{ fontSize: 20, color: 'error.main' }} />
                                {channel.channelName || 'N/A'}
                              </Link>
                            </TableCell>
                            <TableCell>{formatSubscriberCount(channel.subscriberCount)}</TableCell>
                            <TableCell>{channel.viewCount?.toLocaleString() || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </StyledPaper>
            )}

            {/* Failed Status */}
            {jobStatus?.status === 'failed' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Job failed. Please check the errors above and try again.
              </Alert>
            )}
          </Box>
        )}
      </StyledContainer>
    </Container>
  );
}

export default SimilarChannels;
